import {
    computed,
    onBeforeUnmount,
    ref,
    toValue,
    type ComputedRef,
    type MaybeRef,
} from 'vue';

/** 默认面板宽度（px）。 */
const DEFAULT_PANEL_WIDTH = 320;

/** 默认面板预估高度（px），用于拖拽边界钳制。 */
const DEFAULT_PANEL_ESTIMATED_HEIGHT = 260;

/** 默认左上角锚点内边距（px）。 */
const DEFAULT_TOP_LEFT_VIEWPORT_INSET = 12;

/**
 * 浮动面板默认锚点位置。
 */
export type FloatingPanelPlacement = 'center' | 'top-left';

/**
 * 左上角锚点视口内边距（仅决定浮窗初始停靠位置，不参与拖拽下限钳制）。
 */
export interface FloatingPanelViewportInset {
    /** 初始位置：距视口顶部的内边距（px）。 */
    top: number;
    /** 初始位置：距视口左侧的内边距（px）。 */
    left: number;
}

/**
 * 浮动层拖拽配置。
 */
export interface FloatingDialogDragOptions {
    /** 面板宽度（px），默认 320。 */
    width?: MaybeRef<number>;
    /** 面板预估高度（px），仅用于边界钳制，默认 260。 */
    estimatedHeight?: MaybeRef<number>;
    /** 是否把 width 写入 shellStyle，默认 true。 */
    includeWidthInShellStyle?: boolean;
    /** 是否允许拖拽，默认 true。 */
    draggable?: MaybeRef<boolean>;
    /** 默认锚点位置，默认居中。 */
    placement?: MaybeRef<FloatingPanelPlacement>;
    /** 左上角锚点时的初始内边距（拖拽钳制始终按整页视口计算）。 */
    viewportInset?: MaybeRef<FloatingPanelViewportInset>;
    /**
     * 自定义拖拽启动判断。
     *
     * @param event 标题栏 pointerdown 事件。
     * @returns 返回 false 时不启动拖拽。
     */
    shouldStartDrag?: (event: PointerEvent) => boolean;
}

/**
 * 浮动弹窗拖拽控制器。
 */
export interface FloatingDialogDragController {
    /** 当前弹窗面板样式。 */
    shellStyle: ComputedRef<Record<string, string>>;
    /** 标题栏按下时启动拖拽。 */
    handlePointerDown: (event: PointerEvent) => void;
    /** 重置弹窗位置。 */
    resetPosition: () => void;
    /**
     * 运行时更新面板尺寸。
     *
     * 设计说明：当宿主未通过响应式 options 传入尺寸时，可通过该方法更新钳制边界。
     *
     * @param patch 尺寸补丁。
     */
    updateDimensions: (patch: { width?: number; estimatedHeight?: number }) => void;
}

/**
 * 解析面板宽度。
 *
 * @param options 拖拽配置。
 * @param widthOverride 运行时宽度覆盖值。
 * @returns 面板宽度（px）。
 */
function resolvePanelWidth(
    options: FloatingDialogDragOptions | undefined,
    widthOverride: number | undefined,
): number {
    if (widthOverride !== undefined) {
        return widthOverride;
    }
    const configuredWidth = toValue(options?.width);
    return configuredWidth ?? DEFAULT_PANEL_WIDTH;
}

/**
 * 解析面板预估高度。
 *
 * @param options 拖拽配置。
 * @param estimatedHeightOverride 运行时高度覆盖值。
 * @returns 面板预估高度（px）。
 */
function resolvePanelEstimatedHeight(
    options: FloatingDialogDragOptions | undefined,
    estimatedHeightOverride: number | undefined,
): number {
    if (estimatedHeightOverride !== undefined) {
        return estimatedHeightOverride;
    }
    const configuredHeight = toValue(options?.estimatedHeight);
    return configuredHeight ?? DEFAULT_PANEL_ESTIMATED_HEIGHT;
}

/**
 * 创建浮动弹窗拖拽控制器。
 *
 * 设计说明：
 * 1. 弹窗拖拽是纯 UI 行为，不应把 DOM 监听散落到具体业务弹窗中；
 * 2. 这里只维护位置偏移与全局 pointer 生命周期，业务组件只负责声明“哪个区域可拖动”；
 * 3. 关闭弹窗或组件卸载时统一释放监听，避免残留全局回调；
 * 4. 无参调用保持历史默认尺寸，确保现有调用方行为不变。
 *
 * @param options 拖拽配置。
 * @returns 拖拽控制器。
 */
export function useFloatingDialogDrag(
    options?: FloatingDialogDragOptions,
): FloatingDialogDragController {
    const includeWidthInShellStyle = options?.includeWidthInShellStyle ?? true;
    const offsetX = ref<number>(0);
    const offsetY = ref<number>(0);
    const widthOverride = ref<number | undefined>(undefined);
    const estimatedHeightOverride = ref<number | undefined>(undefined);

    let isDragging = false;
    let activePointerId: number | null = null;
    let pointerCaptureTarget: Element | null = null;
    let dragStartClientX = 0;
    let dragStartClientY = 0;
    let dragStartOffsetX = 0;
    let dragStartOffsetY = 0;

    /**
     * 读取当前面板宽度。
     *
     * @returns 面板宽度（px）。
     */
    function getPanelWidth(): number {
        return resolvePanelWidth(options, widthOverride.value);
    }

    /**
     * 读取当前面板预估高度。
     *
     * @returns 面板预估高度（px）。
     */
    function getPanelEstimatedHeight(): number {
        return resolvePanelEstimatedHeight(options, estimatedHeightOverride.value);
    }

    /**
     * 停止当前拖拽并释放全局监听。
     */
    function stopDragging(): void {
        if (pointerCaptureTarget && activePointerId !== null) {
            try {
                (pointerCaptureTarget as HTMLElement).releasePointerCapture?.(activePointerId);
            } catch {
                /**
                 * 指针可能已被浏览器释放；这里的释放失败不影响拖拽状态收口。
                 */
            }
        }
        isDragging = false;
        activePointerId = null;
        pointerCaptureTarget = null;
        window.removeEventListener('pointermove', handlePointerMove, true);
        window.removeEventListener('pointerup', handlePointerUp, true);
        window.removeEventListener('pointercancel', handlePointerUp, true);
    }

    /**
     * 限制弹框偏移，避免标题栏被拖出视口后无法再拿回。
     *
     * 设计说明：
     * 1. 居中锚点：以视口中心为原点，对称钳制到网页四边；
     * 2. 左上角锚点：以初始内边距为偏移原点，但钳制范围覆盖整页视口，
     *    允许向左/上拖回至网页边缘，而非仅限“初始位置右下方”。
     *
     * @param nextX 目标 X 偏移。
     * @param nextY 目标 Y 偏移。
     * @returns 已钳制的偏移。
     */
    function clampOffset(nextX: number, nextY: number): { x: number; y: number } {
        const panelWidth = getPanelWidth();
        const panelEstimatedHeight = getPanelEstimatedHeight();
        const placement = toValue(options?.placement) ?? 'center';

        if (placement === 'top-left') {
            const inset = toValue(options?.viewportInset) ?? {
                top: DEFAULT_TOP_LEFT_VIEWPORT_INSET,
                left: DEFAULT_TOP_LEFT_VIEWPORT_INSET,
            };
            // 面板屏幕坐标 = 锚点(inset) + offset；钳制到 [0, innerWidth/Height - panelSize]
            const minX = -inset.left;
            const minY = -inset.top;
            const maxX = Math.max(minX, window.innerWidth - panelWidth - inset.left);
            const maxY = Math.max(minY, window.innerHeight - panelEstimatedHeight - inset.top);
            return {
                x: Math.min(maxX, Math.max(minX, nextX)),
                y: Math.min(maxY, Math.max(minY, nextY)),
            };
        }

        const maxX = Math.max(0, window.innerWidth / 2 - panelWidth / 2);
        const maxY = Math.max(0, window.innerHeight / 2 - panelEstimatedHeight / 2);
        return {
            x: Math.min(maxX, Math.max(-maxX, nextX)),
            y: Math.min(maxY, Math.max(-maxY, nextY)),
        };
    }

    /**
     * 处理拖拽过程中的指针移动。
     *
     * @param event 指针事件。
     */
    function handlePointerMove(event: PointerEvent): void {
        if (!isDragging) {
            return;
        }

        /**
         * 直接以视口位移计算偏移量，避免依赖弹窗当前 DOM 布局细节，
         * 让同一套控制器可以复用到其它居中浮层。
         */
        const nextOffset = clampOffset(
            dragStartOffsetX + (event.clientX - dragStartClientX),
            dragStartOffsetY + (event.clientY - dragStartClientY),
        );
        offsetX.value = nextOffset.x;
        offsetY.value = nextOffset.y;
    }

    /**
     * 处理拖拽结束。
     */
    function handlePointerUp(): void {
        stopDragging();
    }

    /**
     * 启动拖拽。
     *
     * @param event 标题栏指针按下事件。
     */
    function handlePointerDown(event: PointerEvent): void {
        if (event.button !== 0) {
            return;
        }

        const draggable = toValue(options?.draggable);
        if (draggable === false) {
            return;
        }

        if (options?.shouldStartDrag && !options.shouldStartDrag(event)) {
            return;
        }

        dragStartClientX = event.clientX;
        dragStartClientY = event.clientY;
        dragStartOffsetX = offsetX.value;
        dragStartOffsetY = offsetY.value;
        activePointerId = event.pointerId;
        pointerCaptureTarget = event.currentTarget instanceof Element ? event.currentTarget : null;
        isDragging = true;

        if (pointerCaptureTarget) {
            try {
                (pointerCaptureTarget as HTMLElement).setPointerCapture?.(event.pointerId);
            } catch {
                /**
                 * 部分测试环境或旧浏览器不支持 pointer capture；
                 * 全局 window 监听仍能保证拖拽继续工作。
                 */
            }
        }
        window.addEventListener('pointermove', handlePointerMove, true);
        window.addEventListener('pointerup', handlePointerUp, true);
        window.addEventListener('pointercancel', handlePointerUp, true);
        event.preventDefault();
    }

    /**
     * 重置弹窗到默认锚点位置。
     */
    function resetPosition(): void {
        offsetX.value = 0;
        offsetY.value = 0;
        stopDragging();
    }

    /**
     * 运行时更新面板尺寸。
     *
     * @param patch 尺寸补丁。
     */
    function updateDimensions(patch: { width?: number; estimatedHeight?: number }): void {
        if (patch.width !== undefined) {
            widthOverride.value = patch.width;
        }
        if (patch.estimatedHeight !== undefined) {
            estimatedHeightOverride.value = patch.estimatedHeight;
        }
    }

    /**
     * 拖拽控制器只维护“相对默认锚点位置”的偏移量。
     * 真实布局交给宿主容器负责，避免受 modal 内部内容流影响而偏到视口边缘。
     */
    const shellStyle = computed<Record<string, string>>(() => {
        const style: Record<string, string> = {
            transform: `translate(${offsetX.value}px, ${offsetY.value}px)`,
        };
        if (includeWidthInShellStyle) {
            style.width = `${getPanelWidth()}px`;
        }
        return style;
    });

    onBeforeUnmount(() => {
        stopDragging();
    });

    return {
        shellStyle,
        handlePointerDown,
        resetPosition,
        updateDimensions,
    };
}
